import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import Markdown from 'react-markdown';
import Button from 'components/Button';
import LocalIcon from 'ui/LocalIcon';
import i18n from 'i18n';
import {
  AI_GPT_MODEL_OPTIONS,
  DEFAULT_AI_GPT_MODEL,
  formatAiCompletionDuration,
} from 'api/chatbot-api/aiModels';
import {
  ChatBubble,
  ChatEmptyState,
  ChatInputArea,
  ChatMessageGroup,
  ChatMessages,
  ChatTextarea,
  DrawerBody,
  DrawerHeader,
  DrawerMainHeader,
  DrawerOverlay,
  DrawerPanel,
  DrawerTitle,
  DrawerTitleGroup,
  LoadingDots,
  MarkdownBubbleContent,
  ResponseTime,
  ResponseTimeValue,
  SendButtonRow,
  drawerTransitionName,
} from './styles';
import Select, { IOption } from 'components/Select/Select';
import { handleQuestionFromChatBotStreamMode } from 'api/chatbot-api/AskQuestion';
import Input from 'components/Input';
import { border1 } from 'utils/style/colors';

export type AnalyseViaAIDrawerData = {
  [queryKey: string]: Array<Record<string, unknown>>;
} | null;

interface IAnalyseViaAIDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  data: AnalyseViaAIDrawerData;
  reportTitle?: string;
  surveyTitle?: string;
}

type ChatMessage = {
  id: string;
  text: string;
  isUser: boolean;
  durationMs?: number;
  isStreaming?: boolean;
};

const AnalyseViaAIDrawer: React.FC<IAnalyseViaAIDrawerProps> = ({
  isOpen,
  onClose,
  data: chartData,
  reportTitle,
  surveyTitle,
}) => {
  const [prompt, setPrompt] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [aiGptModel, setAiGptModel] = useState(DEFAULT_AI_GPT_MODEL);
  const [isFetching, setIsFetching] = useState(false);
  const chartDataRef = React.useRef(chartData);
  const chatMessagesRef = useRef<HTMLDivElement>(null);
  const [enableSystemPrompt, setEnableSystemPrompt] = useState<boolean>(false);
  const [systemPrompt, setSystemPrompt] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      chartDataRef.current = chartData;
    }
  }, [isOpen, chartData]);

  useEffect(() => {
    if (!isOpen) {
      setPrompt('');
      setMessages([]);
      setAiGptModel(DEFAULT_AI_GPT_MODEL);
    }
  }, [isOpen]);

  useEffect(() => {
    const container = chatMessagesRef.current;
    if (!container) {
      return;
    }

    container.scrollTo({
      top: container.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages, isFetching]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  const handleSend = useCallback(async () => {
    const trimmedPrompt = prompt.trim();
    if (!trimmedPrompt || isFetching) {
      return;
    }

    const now = Date.now();
    const assistantMessageId = `${now}-assistant`;

    setMessages((prev) => [
      ...prev,
      {
        id: `${now}`,
        text: trimmedPrompt,
        isUser: true,
      },
      {
        id: assistantMessageId,
        text: '',
        isUser: false,
        isStreaming: true,
      },
    ]);
    setPrompt('');
    setIsFetching(true);

    const footerSystemPrompt = ` \n\n
    chart data: ${JSON.stringify(chartDataRef.current)} \n
    survey title: ${surveyTitle} \n
    report title: ${reportTitle} \n

    Finally get back your result in Markdown format.`;

    const systemInstruction =
      (enableSystemPrompt && systemPrompt.length > 0
        ? systemPrompt
        : `You are a helpful analyser that can analyse the chart data and answer questions and help with tasks.
    You are given a chart data and a question.
    You need to answer the question based on the chart data.
    You need to use the chart data to answer the question.
    You need to use the chart data to help with the task.
    You need to use the chart data to help with the task.`) +
      footerSystemPrompt;

    const instructions = [
      {
        role: 'developer',
        content: [
          {
            type: 'input_text',
            text: systemInstruction,
          },
        ],
      },
      ...messages.map((message) => ({
        role: message.isUser ? 'user' : 'assistant',
        content: [
          {
            type: message.isUser ? 'input_text' : 'output_text',
            text: message.text,
          },
        ],
      })),
      {
        role: 'user',
        content: [
          {
            type: 'input_text',
            text: trimmedPrompt,
          },
        ],
      },
    ];

    try {
      const { text, durationMs } = await handleQuestionFromChatBotStreamMode(
        instructions,
        true,
        aiGptModel,
        {
          onTextDelta: (streamText) => {
            setMessages((prev) =>
              prev.map((message) =>
                message.id === assistantMessageId
                  ? { ...message, text: streamText }
                  : message
              )
            );
          },
        }
      );

      setMessages((prev) =>
        prev.map((message) =>
          message.id === assistantMessageId
            ? {
                ...message,
                text: text ?? message.text,
                durationMs,
                isStreaming: false,
              }
            : message
        )
      );
    } finally {
      setIsFetching(false);
    }
  }, [prompt, isFetching, messages, aiGptModel, reportTitle, surveyTitle]);

  const handlePromptKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  return (
    <CSSTransition
      in={isOpen}
      timeout={350}
      classNames={drawerTransitionName}
      unmountOnExit
    >
      <DrawerOverlay onClick={onClose} onKeyDown={handleKeyDown} tabIndex={-1}>
        <DrawerPanel
          onClick={(event: React.MouseEvent) => event.stopPropagation()}
        >
          <DrawerHeader>
            <DrawerMainHeader>
              <DrawerTitleGroup>
                <LocalIcon
                  iconName="gemini-logo.svg"
                  type="sm"
                  color="no-color"
                  style={{ margin: 0, flexShrink: 0 }}
                />
                <DrawerTitle>
                  {
                    i18n.t('analyseDataTitle', {
                      ns: 'survey',
                      reportTitle,
                    }) as string
                  }
                </DrawerTitle>
              </DrawerTitleGroup>
              <LocalIcon
                iconName="close-square.svg"
                isLink={true}
                color="no-color"
                type="sm"
                onClick={onClose}
                style={{ margin: 0, flexShrink: 0 }}
              />
            </DrawerMainHeader>
            <DrawerMainHeader>
              <Select
                options={AI_GPT_MODEL_OPTIONS as unknown as IOption[]}
                values={AI_GPT_MODEL_OPTIONS.filter(
                  (el) => el.value === aiGptModel
                )}
                disabled={isFetching}
                onChangeValues={(values: IOption[]) =>
                  setAiGptModel(
                    values?.[0].value?.toString() ?? DEFAULT_AI_GPT_MODEL
                  )
                }
              />
            </DrawerMainHeader>
            <DrawerMainHeader>
              <div>
                <Input
                  id="enable-system-prompt"
                  type="checkbox"
                  label={
                    i18n.t('enableSystemPrompt', {
                      ns: 'survey',
                    }) as string
                  }
                  disabled={isFetching}
                  checked={enableSystemPrompt}
                  onChange={(e) => setEnableSystemPrompt(e.target.checked)}
                />
              </div>
            </DrawerMainHeader>
            <DrawerMainHeader>
              {enableSystemPrompt && (
                <div className="w-full flex flex-col justify-center gap-3 px-5">
                  <label
                    htmlFor="ai-system-text"
                    className="text-sm font-medium text-gray-700"
                  >
                    {i18n.t('systemPrompt', { ns: 'survey' }) as string}
                  </label>
                  <textarea
                    id="ai-system-text"
                    value={systemPrompt}
                    onChange={(e) => setSystemPrompt(e.target.value)}
                    disabled={isFetching}
                    style={{
                      minHeight: '4rem',
                      maxHeight: '8rem',
                      border: border1,
                      borderRadius: '7px',
                      padding: '5px',
                      outline: 'none',
                    }}
                  />
                </div>
              )}
            </DrawerMainHeader>
          </DrawerHeader>

          <DrawerBody>
            <ChatMessages ref={chatMessagesRef}>
              {messages.length === 0 ? (
                <ChatEmptyState>
                  {i18n.t('analyseDataWelcome', { ns: 'survey' }) as string}
                </ChatEmptyState>
              ) : (
                messages.map((message) => (
                  <ChatMessageGroup key={message.id} isUser={message.isUser}>
                    <ChatBubble isUser={message.isUser}>
                      {message.isUser ? (
                        message.text
                      ) : message.text ? (
                        <MarkdownBubbleContent>
                          <Markdown>{message.text}</Markdown>
                        </MarkdownBubbleContent>
                      ) : message.isStreaming ? (
                        <LoadingDots aria-hidden="true">
                          <span />
                          <span />
                          <span />
                        </LoadingDots>
                      ) : null}
                    </ChatBubble>
                    {!message.isUser &&
                      !message.isStreaming &&
                      message.durationMs !== undefined && (
                        <ResponseTime>
                          {
                            i18n.t('aiCompletionTime', {
                              ns: 'survey',
                            }) as string
                          }
                          <ResponseTimeValue>
                            {formatAiCompletionDuration(message.durationMs)}
                          </ResponseTimeValue>
                        </ResponseTime>
                      )}
                  </ChatMessageGroup>
                ))
              )}
            </ChatMessages>

            <ChatInputArea>
              <ChatTextarea
                id="analyse-via-ai-prompt"
                value={prompt}
                disabled={isFetching}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setPrompt(event.target.value)
                }
                onKeyDown={handlePromptKeyDown}
                placeholder={
                  i18n.t('analyseDataInputPlaceholder', {
                    ns: 'survey',
                  }) as string
                }
                rows={3}
              />
              <SendButtonRow>
                <Button
                  type="button"
                  color="blue"
                  loading={isFetching}
                  disabled={!prompt.trim() || isFetching}
                  onClick={handleSend}
                >
                  {i18n.t('analyseDataSend', { ns: 'survey' }) as string}
                </Button>
              </SendButtonRow>
            </ChatInputArea>
          </DrawerBody>
        </DrawerPanel>
      </DrawerOverlay>
    </CSSTransition>
  );
};

export default AnalyseViaAIDrawer;
